import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { usePermissions } from "@/shared/hooks/usePermissions";
import { formatDisplayName } from "@/shared/utils/formatters";
import { announcementService } from "@/features/announcements/services/feedService";
import { sendingOrgService } from "@/features/return-home/services/sendingOrgService";
import { Spinner } from "@/shared/components/ui/Spinner";
import { Button } from "@/shared/components/ui/Button";
import { EmptyState } from "@/shared/components/feedback/EmptyState";
import { AnnouncementCard } from "@/features/announcements/components/AnnouncementCard";
import type { Announcement } from "@/features/announcements/types/feed";

const OFFICER_TYPES = ["officer", "admin"];

const COUNTRY_OPTIONS = [
  { code: "JP", key: "country_jp" },
  { code: "PH", key: "country_ph" },
  { code: "VN", key: "country_vn" },
  { code: "ID", key: "country_id" },
  { code: "MM", key: "country_mm" },
  { code: "KH", key: "country_kh" },
  { code: "TH", key: "country_th" },
  { code: "CN", key: "country_cn" },
  { code: "NP", key: "country_np" },
  { code: "IN", key: "country_in" },
  { code: "BD", key: "country_bd" },
  { code: "LK", key: "country_lk" },
];

export const HomePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { userData } = useAuth();
  const { canWrite } = usePermissions();
  const focusId =
    (location.state as { focusId?: string })?.focusId ??
    new URLSearchParams(location.search).get("announcement") ??
    null;
  const isOfficer = OFFICER_TYPES.includes(
    userData?.userType?.toLowerCase() || "",
  );
  const isSouser = userData?.userType?.toLowerCase() === "souser";

  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [companyFilter, setCompanyFilter] = useState("");
  const [batchFilter, setBatchFilter] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [sendingOrgFilter, setSendingOrgFilter] = useState("");
  const [sendingOrgs, setSendingOrgs] = useState<{ value: string; label: string }[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [surveysOnly, setSurveysOnly] = useState(false);
  const [companyDropdownOpen, setCompanyDropdownOpen] = useState(false);
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [sendingOrgDropdownOpen, setSendingOrgDropdownOpen] = useState(false);
  const companyDropdownRef = useRef<HTMLDivElement>(null);
  const countryDropdownRef = useRef<HTMLDivElement>(null);
  const sendingOrgDropdownRef = useRef<HTMLDivElement>(null);

  const loadAnnouncements = useCallback(async () => {
    try {
      const data = await announcementService.getAnnouncements();
      setAnnouncements(data);
    } catch (error) {
      console.error("Failed to load announcements:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAnnouncements();
  }, [loadAnnouncements]);

  // Fetch sending orgs when countryFilter changes
  useEffect(() => {
    if (countryFilter) {
      sendingOrgService
        .getDropdown(countryFilter)
        .then((data: any[]) =>
          setSendingOrgs(
            data.map((o: any) => ({ value: o.value ?? o.id, label: o.label ?? o.name })),
          ),
        )
        .catch(() => setSendingOrgs([]));
    } else {
      setSendingOrgs([]);
      setSendingOrgFilter("");
    }
  }, [countryFilter]);

  // Auto-scroll to focused announcement from notification deep link or ?announcement= param
  useEffect(() => {
    if (!focusId || loading) return;
    // Clear both router state and query param from the URL
    window.history.replaceState({}, "", window.location.pathname);
    const timer = setTimeout(() => {
      const el = document.getElementById(`announcement-${focusId}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [focusId, loading]);

  // Only active announcements are shown — inactive are removed from list entirely
  const activeAnnouncements = announcements.filter((a) => a.active);
  // Open surveys the viewer has not answered yet — drives the nudge banner.
  const pendingSurveys = activeAnnouncements.filter((a) => a.poll?.is_open && !a.poll.has_responded).length;

  // ─── Helper: extract company label from polymorphic entry ───
  const getCompanyLabel = (c: any): string =>
    typeof c === "string" ? c : c?.label || c?.company_name || "";

  // ─── Derive unique company names from data ───
  const uniqueCompanies = useMemo(() => {
    const set = new Set<string>();
    activeAnnouncements.forEach((a) => {
      (a.target_companies || a.company || []).forEach((c: any) => {
        const label = getCompanyLabel(c);
        if (label) set.add(label);
      });
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [activeAnnouncements]);

  // Close dropdowns on outside click
  useEffect(() => {
    if (!companyDropdownOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (companyDropdownRef.current && !companyDropdownRef.current.contains(e.target as Node)) {
        setCompanyDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [companyDropdownOpen]);

  useEffect(() => {
    if (!countryDropdownOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(e.target as Node)) {
        setCountryDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [countryDropdownOpen]);

  useEffect(() => {
    if (!sendingOrgDropdownOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (sendingOrgDropdownRef.current && !sendingOrgDropdownRef.current.contains(e.target as Node)) {
        setSendingOrgDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [sendingOrgDropdownOpen]);

  const handleFavoriteToggle = (rowId: number, isFavorited: boolean) => {
    setAnnouncements((prev) =>
      prev.map((a) => (a.row_id === rowId ? { ...a, is_favorited: isFavorited } : a)),
    );
  };

  const hasActiveFilters =
    companyFilter !== "" || batchFilter !== "" || countryFilter !== "" || sendingOrgFilter !== "" || favoritesOnly || surveysOnly;

  // ─── Filter: text search + company + batch + country + sending_org + favorites (all AND-ed) ───
  const lowerSearch = search.toLowerCase().trim();
  const filtered = activeAnnouncements.filter((a) => {
    // Favorites filter
    if (favoritesOnly && !a.is_favorited) return false;
    if (surveysOnly && !a.poll) return false;

    // Text search (title + content_text)
    if (lowerSearch) {
      const title = (a.title || "").toLowerCase();
      const content = (a.content_text || "").toLowerCase();
      if (!title.includes(lowerSearch) && !content.includes(lowerSearch)) return false;
    }

    // Company filter (exact match from dropdown)
    if (companyFilter) {
      const companies = (a.target_companies || a.company || []).map((c: any) =>
        getCompanyLabel(c),
      );
      if (!companies.includes(companyFilter)) return false;
    }

    // Batch filter (free text, case-insensitive)
    if (batchFilter) {
      if (!a.batch_no?.toLowerCase().includes(batchFilter.toLowerCase())) return false;
    }

    // Country filter — if announcement has countries set, selected must be included
    if (countryFilter) {
      const countries: string[] = a.country || [];
      if (countries.length > 0 && !countries.includes(countryFilter)) return false;
    }

    // Sending org filter — if announcement has a sending_org set, must match
    if (sendingOrgFilter) {
      if (a.sending_org && a.sending_org !== sendingOrgFilter) return false;
    }

    return true;
  });

  const selectedCountryLabel = COUNTRY_OPTIONS.find((c) => c.code === countryFilter);

  if (loading) return <Spinner />;

  return (
    <div>
      <div className="flex flex-col gap-3 mb-4 md:flex-row md:items-center md:justify-between md:mb-6">
        <h1 className="text-xl md:text-2xl font-bold text-gray-900">{t("home.title")}</h1>
        {canWrite("announcements") && (
          <Button onClick={() => navigate("/announcements/new")} className="w-full md:w-auto">
            {t("feed.create")}
          </Button>
        )}
      </div>

      {/* ── Search & Filters ── */}
      <div className="mb-4 md:mb-6 space-y-2">
        {/* Row: search input + filter toggle */}
        <div className="flex gap-2">
          <div className="relative flex-1 md:max-w-md">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder={t("feed.search_announcements")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-9 py-2.5 md:py-2 border border-gray-300 rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          <button
            onClick={() => setFavoritesOnly((v) => !v)}
            title={t("feed.favorites_only")}
            className={`flex items-center gap-1.5 px-3 py-2.5 md:py-2 border rounded-lg text-sm font-medium transition-colors shrink-0 ${
              favoritesOnly
                ? "border-amber-300 bg-amber-50 text-amber-600"
                : "border-gray-300 bg-white text-gray-500 hover:bg-gray-50 hover:text-amber-400"
            }`}
          >
            <svg
              className="w-4 h-4"
              viewBox="0 0 24 24"
              fill={favoritesOnly ? "currentColor" : "none"}
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
              />
            </svg>
            <span className="hidden md:inline">{t("feed.favorites_only")}</span>
          </button>
          <button
            onClick={() => setSurveysOnly((v) => !v)}
            title={t("feed.poll.surveys_only")}
            className={`flex items-center gap-1.5 px-3 py-2.5 md:py-2 border rounded-lg text-sm font-medium transition-colors shrink-0 ${
              surveysOnly
                ? "border-violet-300 bg-violet-50 text-violet-700"
                : "border-gray-300 bg-white text-gray-500 hover:bg-gray-50 hover:text-violet-500"
            }`}
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 20V10m6 10V4m6 16v-7m4 7H2" />
            </svg>
            <span className="hidden md:inline">{t("feed.poll.surveys_only")}</span>
          </button>
          {!isSouser && (
            <button
              onClick={() => setShowFilters((v) => !v)}
              className={`flex items-center gap-1.5 px-3 py-2.5 md:py-2 border rounded-lg text-sm font-medium transition-colors shrink-0 ${
                showFilters || (hasActiveFilters && !favoritesOnly)
                  ? "border-indigo-300 bg-indigo-50 text-indigo-700"
                  : "border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
              }`}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              <span className="hidden md:inline">{t("feed.search_filter")}</span>
              {hasActiveFilters && !favoritesOnly && (
                <span className="w-2 h-2 rounded-full bg-indigo-500" />
              )}
            </button>
          )}
        </div>

        {/* ── Filter rows ── */}
        {showFilters && (
          <div className="flex flex-col gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">

            {/* Row 1: Company + Batch */}
            {!isSouser && (
              <div className="flex flex-col sm:flex-row gap-2">
                {/* Company select */}
                <div className="relative flex-1" ref={companyDropdownRef}>
                  <button
                    type="button"
                    onClick={() => setCompanyDropdownOpen((v) => !v)}
                    className={`w-full flex items-center justify-between gap-2 px-3 py-2 border rounded-lg text-sm text-left transition-colors ${
                      companyFilter
                        ? "border-indigo-300 bg-white text-gray-900"
                        : "border-gray-300 bg-white text-gray-500"
                    }`}
                  >
                    <span className="truncate">
                      {companyFilter || t("feed.filter_company", "All companies")}
                    </span>
                    <svg className={`w-4 h-4 shrink-0 text-gray-400 transition-transform ${companyDropdownOpen ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {companyDropdownOpen && (
                    <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
                      <button
                        type="button"
                        onClick={() => { setCompanyFilter(""); setCompanyDropdownOpen(false); }}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                          !companyFilter ? "text-indigo-700 font-medium bg-indigo-50" : "text-gray-700"
                        }`}
                      >
                        {t("feed.filter_company", "All companies")}
                      </button>
                      {uniqueCompanies.map((c) => (
                        <button
                          key={c}
                          type="button"
                          onClick={() => { setCompanyFilter(c); setCompanyDropdownOpen(false); }}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                            companyFilter === c ? "text-indigo-700 font-medium bg-indigo-50" : "text-gray-700"
                          }`}
                        >
                          {c}
                        </button>
                      ))}
                      {uniqueCompanies.length === 0 && (
                        <p className="px-3 py-2 text-xs text-gray-400">{t("common.no_data", "No data")}</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Batch filter */}
                <div className="relative flex-1">
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={batchFilter}
                    onChange={(e) => setBatchFilter(e.target.value.replace(/\D/g, ""))}
                    placeholder={t("feed.filter_batch")}
                    className={`w-full px-3 py-2 border rounded-lg text-sm transition-colors ${
                      batchFilter
                        ? "border-indigo-300 text-gray-900"
                        : "border-gray-300 text-gray-500"
                    }`}
                  />
                </div>
              </div>
            )}

            {/* Row 2: Country + Sending Org */}
            <div className="flex flex-col sm:flex-row gap-2">
              {/* Country filter */}
              <div className="relative flex-1" ref={countryDropdownRef}>
                <button
                  type="button"
                  onClick={() => setCountryDropdownOpen((v) => !v)}
                  className={`w-full flex items-center justify-between gap-2 px-3 py-2 border rounded-lg text-sm text-left transition-colors ${
                    countryFilter
                      ? "border-indigo-300 bg-white text-gray-900"
                      : "border-gray-300 bg-white text-gray-500"
                  }`}
                >
                  <span className="truncate">
                    {selectedCountryLabel
                      ? t(`profile.${selectedCountryLabel.key}`)
                      : t("feed.filter_country")}
                  </span>
                  <svg className={`w-4 h-4 shrink-0 text-gray-400 transition-transform ${countryDropdownOpen ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {countryDropdownOpen && (
                  <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
                    <button
                      type="button"
                      onClick={() => { setCountryFilter(""); setCountryDropdownOpen(false); }}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                        !countryFilter ? "text-indigo-700 font-medium bg-indigo-50" : "text-gray-700"
                      }`}
                    >
                      {t("feed.filter_country")}
                    </button>
                    {COUNTRY_OPTIONS.map((opt) => (
                      <button
                        key={opt.code}
                        type="button"
                        onClick={() => { setCountryFilter(opt.code); setCountryDropdownOpen(false); }}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                          countryFilter === opt.code ? "text-indigo-700 font-medium bg-indigo-50" : "text-gray-700"
                        }`}
                      >
                        {t(`profile.${opt.key}`)}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Sending org filter (only when country selected and orgs available) */}
              {countryFilter && sendingOrgs.length > 0 ? (
                <div className="relative flex-1" ref={sendingOrgDropdownRef}>
                  <button
                    type="button"
                    onClick={() => setSendingOrgDropdownOpen((v) => !v)}
                    className={`w-full flex items-center justify-between gap-2 px-3 py-2 border rounded-lg text-sm text-left transition-colors ${
                      sendingOrgFilter
                        ? "border-indigo-300 bg-white text-gray-900"
                        : "border-gray-300 bg-white text-gray-500"
                    }`}
                  >
                    <span className="truncate">
                      {sendingOrgs.find((o) => o.value === sendingOrgFilter)?.label ||
                        t("feed.form.sending_org_all")}
                    </span>
                    <svg className={`w-4 h-4 shrink-0 text-gray-400 transition-transform ${sendingOrgDropdownOpen ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {sendingOrgDropdownOpen && (
                    <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
                      <button
                        type="button"
                        onClick={() => { setSendingOrgFilter(""); setSendingOrgDropdownOpen(false); }}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                          !sendingOrgFilter ? "text-indigo-700 font-medium bg-indigo-50" : "text-gray-700"
                        }`}
                      >
                        {t("feed.form.sending_org_all")}
                      </button>
                      {sendingOrgs.map((org) => (
                        <button
                          key={org.value}
                          type="button"
                          onClick={() => { setSendingOrgFilter(org.value); setSendingOrgDropdownOpen(false); }}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                            sendingOrgFilter === org.value
                              ? "text-indigo-700 font-medium bg-indigo-50"
                              : "text-gray-700"
                          }`}
                        >
                          {org.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex-1 hidden sm:block" />
              )}
            </div>

            {/* Clear filters */}
            {hasActiveFilters && (
              <button
                onClick={() => {
                  setCompanyFilter("");
                  setBatchFilter("");
                  setCountryFilter("");
                  setSendingOrgFilter("");
                  setFavoritesOnly(false);
                }}
                className="self-start flex items-center gap-1 px-3 py-2 text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                {t("feed.management.clear_filters", "Clear")}
              </button>
            )}
          </div>
        )}

        {/* Active filter pills (shown when filters are collapsed but active) */}
        {!showFilters && hasActiveFilters && (
          <div className="flex flex-wrap items-center gap-1.5">
            {!isSouser && companyFilter && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-indigo-50 text-indigo-700 text-xs font-medium rounded-full">
                {companyFilter}
                <button onClick={() => setCompanyFilter("")} className="hover:text-indigo-900">
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </span>
            )}
            {!isSouser && batchFilter && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-50 text-amber-700 text-xs font-medium rounded-full">
                {batchFilter}
                <button onClick={() => setBatchFilter("")} className="hover:text-amber-900">
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </span>
            )}
            {countryFilter && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-50 text-green-700 text-xs font-medium rounded-full">
                {selectedCountryLabel ? t(`profile.${selectedCountryLabel.key}`) : countryFilter}
                <button onClick={() => setCountryFilter("")} className="hover:text-green-900">
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </span>
            )}
            {sendingOrgFilter && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-purple-50 text-purple-700 text-xs font-medium rounded-full">
                {sendingOrgs.find((o) => o.value === sendingOrgFilter)?.label || sendingOrgFilter}
                <button onClick={() => setSendingOrgFilter("")} className="hover:text-purple-900">
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </span>
            )}
          </div>
        )}
      </div>

      {pendingSurveys > 0 && !surveysOnly && (
        <button
          type="button"
          onClick={() => setSurveysOnly(true)}
          className="mb-4 flex w-full items-center gap-2 rounded-lg border border-violet-200 bg-violet-50 px-4 py-2.5 text-left text-sm font-medium text-violet-800 hover:bg-violet-100"
        >
          <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 20V10m6 10V4m6 16v-7m4 7H2" /></svg>
          <span className="flex-1">{t("feed.poll.pending_banner", { count: pendingSurveys })}</span>
          <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
        </button>
      )}

      {filtered.length === 0 ? (
        <EmptyState title={t("home.no_announcements")} />
      ) : (
        <div className="space-y-4">
          {filtered.map((announcement) => (
            <AnnouncementCard
              key={announcement.row_id}
              announcement={announcement}
              isOfficer={isOfficer}
              currentUserId={userData?.id ?? ""}
              currentUserName={formatDisplayName(userData?.lastName, userData?.firstName, userData?.middleName)}
              onEdit={(a) => navigate(`/announcements/edit/${a.row_id}`)}
              onFavoriteToggle={handleFavoriteToggle}
              onPollChange={(poll) => setAnnouncements((prev) => prev.map((item) => item.row_id === announcement.row_id ? { ...item, poll } : item))}
              isFocused={String(announcement.row_id) === focusId}
            />
          ))}
        </div>
      )}

    </div>
  );
};
